/* VIP trial countdown banner.
 *
 * Renders at the very top of AppShell when the user is on an active VIP
 * comp grant. Auto-color-codes by urgency:
 *   - ok (>7 days): no banner — they have plenty of time
 *   - warn (1-7 days): amber banner
 *   - urgent (<1 day): red banner
 *
 * One-click upgrade CTA → /app/billing.
 */
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, AlertTriangle } from "lucide-react";

const TONE = {
  warn: {
    bg: "bg-amber-50 border-amber-200 text-amber-900",
    ctaBg: "bg-amber-600 hover:bg-amber-700",
    Icon: Sparkles,
  },
  urgent: {
    bg: "bg-rose-50 border-rose-300 text-rose-900",
    ctaBg: "bg-rose-600 hover:bg-rose-700",
    Icon: AlertTriangle,
  },
};

export default function VipTrialBanner() {
  const { user } = useAuth();
  const trial = user?.vip_trial;
  if (!trial || trial.urgency === "ok") return null;

  const tone = TONE[trial.urgency] || TONE.warn;
  const days = Math.max(0, Math.ceil(trial.days_left));
  const tierLabel = (trial.tier || "Pro")
    .replace(/^\w/, (c) => c.toUpperCase());

  const message = days <= 1
    ? `Tomorrow your ${tierLabel} VIP access ends — you'll downgrade to Starter.`
    : `Your ${tierLabel} VIP trial ends in ${days} days.`;

  return (
    <div
      className={`border-b px-4 py-2.5 ${tone.bg}`}
      data-testid="vip-trial-banner"
    >
      <div className="flex items-center gap-3 max-w-7xl mx-auto">
        <tone.Icon size={16} className="shrink-0" />
        <div className="text-sm font-medium flex-1 min-w-0">
          {message}{" "}
          <span className="opacity-80 hidden sm:inline">
            Keep your leads, pipeline, and recordings — lock in your rate now.
          </span>
        </div>
        <Link
          to="/v3/business/billing"
          className={`shrink-0 text-white font-bold text-xs px-3 py-1.5 rounded ${tone.ctaBg}`}
          data-testid="vip-trial-banner-cta"
        >
          Upgrade →
        </Link>
      </div>
    </div>
  );
}
